import { z } from 'zod';
import type { EditCommand } from './commands';
import { videoClipsOf } from './captions';
import { DEFAULT_TRACK_IDS, type CropRect, type Edl } from './edl';
import { apply } from './reducer';

/**
 * 웹캠 보존 — 세로로 자를 때 얼굴이 사라지지 않게 한다.
 *
 * 🔴 결과물 리뷰 1순위(`docs/shorts-editor-input-redesign.md` §11.1-1, §11.8).
 * 4명 중 3명이 같은 것을 지목했다. 실사용자 원문 —
 * "가운데를 세로로 자르면 **내 얼굴이 통째로 날아간다.** 그럼 그냥 게임 플레이 영상에
 * 자막 붙은 거다. **내 클립인 이유가 없어진다.** … 이거 하나 되면 최소한 «내 클립»은 된다 —
 * 나머지 없는 것들은 촌스러운 정도지, 이건 아예 다른 사람 영상이다."
 *
 * **접근: PIP.** 메인 영상(게임)은 지금처럼 꽉 채우고, 웹캠 영역만 잘라 구석에 얹는다.
 * 왜 2단 레이아웃(위 게임 / 아래 얼굴)이 아닌가 — 그건 새 레이아웃 프리셋과 렌더러 변경이
 * 필요한데, PIP 는 **이미 끝까지 동작하는 broll 트랙**을 그대로 쓴다(reducer·렌더러 양쪽 확인).
 * 가장 적은 변경으로 «내 클립인 이유»를 되돌린다.
 *
 * 얼굴 인식을 돌리지 않는다 — 이유는 `WebcamRect` 주석 참조(OBS 레이아웃은 고정이다).
 */

/** 소스 프레임 기준 웹캠 영역 (0..1). */
export const webcamRectSchema = z
  .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().min(0.02).max(1),
    h: z.number().min(0.02).max(1),
  })
  .refine((r) => r.x + r.w <= 1 && r.y + r.h <= 1, {
    message: '웹캠 영역이 화면 밖으로 나갑니다',
  });

export type WebcamRect = z.infer<typeof webcamRectSchema>;

/** 캔버스 위 PIP 자리. 세로 화면에서 실제로 안 가리는 곳만 준다. */
export const PIP_PLACEMENTS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const;
export type PipPlacement = (typeof PIP_PLACEMENTS)[number];

/**
 * PIP 가 캔버스에서 차지하는 너비 비율.
 *
 * 0.32 근거: 이보다 작으면 세로 화면에서 표정이 안 읽히고, 크면 게임 화면을 가린다.
 * 쇼츠에서 얼굴 PIP 는 «누가 반응하는지»를 알리는 용도지 주인공이 아니다.
 */
export const PIP_WIDTH = 0.32;

/** 화면 가장자리 여백. 쇼츠 UI 데드존(우측 ~120px ≈ 0.111)을 피한다. */
const PIP_MARGIN_X = 0.04;

/**
 * 세로 위치.
 *
 * 위: 상단 데드존(~180px ≈ 0.094) 아래. 아래: 자막(하단 400px ≈ 0.208)과 하단
 * 데드존(~350px ≈ 0.182) 위. **자막을 가리면 안 된다** — 자막이 이 도구의 본체다.
 */
const PIP_TOP_Y = 0.115;
const PIP_BOTTOM_MARGIN = 0.26;

/** 우측은 액션 버튼 데드존(~0.111)을 더 피한다. */
const PIP_RIGHT_MARGIN = 0.13;

/** 캔버스 비율 (9:16). 소스 크롭 비율을 캔버스 비율로 환산할 때 쓴다. */
const CANVAS_ASPECT = 9 / 16;

/**
 * PIP 가 세로로 먹을 수 있는 최대 비율. 세로로 아주 긴 웹캠 크롭(잘못 등록한 경우 포함)이
 * 화면을 반 넘게 덮는 것을 막는다.
 */
const PIP_MAX_HEIGHT = 0.34;

/**
 * 웹캠 크롭 비율을 유지하면서 캔버스 위 배치 사각형을 만든다.
 *
 * 비율을 안 맞추면 얼굴이 옆으로 늘어난다 — «대충 만든 것»의 대표 증상이다.
 * `rect` 는 캔버스 기준 0..1 이고 캔버스는 9:16 이므로, 소스에서의 가로세로비를
 * 캔버스 좌표계로 환산해야 한다.
 */
export function pipRect(webcam: WebcamRect, placement: PipPlacement, sourceAspect = 16 / 9) {
  const w = PIP_WIDTH;
  // 소스에서 웹캠 조각의 실제 가로세로비.
  const cropAspect = (webcam.w / webcam.h) * sourceAspect;

  // 캔버스 좌표는 폭·높이를 **각각** 1 로 정규화한다. 실제 픽셀은 (w×1080)×(h×1920) 이므로
  //   (w·1080) / (h·1920) = cropAspect  →  h = w · (1080/1920) / cropAspect
  // 즉 CANVAS_ASPECT 를 **곱한다**. 나누면 1/CANVAS_ASPECT² = 3.16배 세로로 늘어난다
  // (적대적 리뷰 🔴1 — 정사각 얼굴이 346×1092px 로 나왔다).
  const raw = (w * CANVAS_ASPECT) / cropAspect;
  // 아주 세로로 긴 크롭이 와도 화면을 다 먹지 않게 먼저 제한한다. **클램프한 h 로 y 를
  // 계산해야** 한다 — 순서를 바꾸면 y=0,h=1 이 되어 PIP 가 아니라 전체 화면이 된다.
  const h = Math.min(raw, PIP_MAX_HEIGHT);
  const x = placement.endsWith('right') ? 1 - PIP_RIGHT_MARGIN - w : PIP_MARGIN_X;
  const y = placement.startsWith('top') ? PIP_TOP_Y : 1 - PIP_BOTTOM_MARGIN - h;
  return { x, y: Math.max(0, Math.min(y, 1 - h)), w, h };
}

/**
 * 이 broll 클립이 **우리가 얹은 웹캠 PIP** 인가.
 *
 * EDL 아이템에 «출처» 필드가 없어서 모양으로 판별한다. 우리가 내는 PIP 는 항상
 * `volume: 0` + `crop` 있음 + `rect.w === PIP_WIDTH` 다. 사람이 쓰는 «B-roll 복제»는
 * 크롭이 없고 폭도 다르다(`Timeline.tsx` 기본값 0.35).
 *
 * ⚠️ 완벽한 판별이 아니다. 사람이 우연히 같은 모양을 만들면 지워진다. 그 대신 반대 실수
 * (남의 컷어웨이를 통째로 날리는 것)를 막는다 — 둘 중 덜 나쁜 쪽을 골랐다.
 *
 * 🔴 **내보낸다.** 에디터도 같은 판별을 해야 한다 — 「얼굴 빼기」가 지우는 것과 썸네일이
 * 보여주는 것이 같은 아이템이어야 하기 때문이다. 판별을 두 군데 적으면 그 둘이 갈리고,
 * 그 순간 «보여준 것과 다른 것을 지우는» 버튼이 된다.
 */
export function isOurPip(item: { volume?: number; crop?: unknown; rect?: { w: number } }): boolean {
  return item.volume === 0 && item.crop !== undefined && item.rect?.w === PIP_WIDTH;
}

export interface WebcamPipPlan {
  edl: Edl;
  commands: EditCommand[];
  /** 얹은 PIP 개수 (= 처리한 메인 클립 수). */
  applied: number;
}

/**
 * 메인 클립마다 같은 구간의 웹캠 조각을 broll 트랙에 얹는다.
 *
 * 컷마다 따로 얹는 이유: 컷 사이에 빈 구간이 생기면 얼굴이 깜빡깜빡 사라진다.
 * 메인 클립과 **같은 소스 구간·같은 길이**를 쓰므로 립싱크가 어긋날 수 없다.
 *
 * broll 클립은 소리를 내지 않아야 한다 — 같은 오디오가 두 번 겹치면 울린다.
 */
export function planWebcamPip(
  edl: Edl,
  args: { webcam: WebcamRect; assetId?: string; placement?: PipPlacement; sourceAspect?: number }
): WebcamPipPlan {
  const targets = videoClipsOf(edl).filter(
    (c) => !args.assetId || c.source.assetId === args.assetId
  );
  if (targets.length === 0) return { edl, commands: [], applied: 0 };

  const rect = pipRect(args.webcam, args.placement ?? 'top-right', args.sourceAspect);
  const crop: CropRect = { x: args.webcam.x, y: args.webcam.y, w: args.webcam.w, h: args.webcam.h };

  let working = edl;
  const commands: EditCommand[] = [];
  let applied = 0;

  // 우리가 예전에 얹은 PIP 만 치운다 — 두 번 부르면 얼굴이 두 개 겹치기 때문이다.
  //
  // 🔴 **broll 트랙을 통째로 비우면 안 된다.** 사람이 «B-roll(PIP) 복제»로 넣어 둔
  // 컷어웨이가 같은 트랙에 산다(`Timeline.tsx` 우클릭 메뉴). 통째로 지우면 사용자가
  // 만든 것을 우리가 말없이 삭제하고 DB 에 저장해 버린다(적대적 리뷰 🔴3).
  const brollTrack = working.tracks.find((t) => t.id === DEFAULT_TRACK_IDS.broll);
  for (const item of brollTrack?.items ?? []) {
    if (item.type !== 'clip' || !isOurPip(item)) continue;
    const cmd: EditCommand = { op: 'remove', itemId: item.id };
    try {
      working = apply(working, cmd);
      commands.push(cmd);
    } catch {
      /* 남아 있어도 아래에서 새로 얹는다 */
    }
  }

  for (const clip of targets) {
    // `addClip` 이 crop·fit·rect·volume 을 한 번에 받는다 — 나눠 보내면 중간 상태에서
    // 실패했을 때 «크롭 안 된 전체 화면»이 얼굴 자리에 얹힌 채로 남는다.
    const add: EditCommand = {
      op: 'addClip',
      trackId: DEFAULT_TRACK_IDS.broll,
      source: { ...clip.source },
      at: clip.from,
      crop,
      rect,
      fit: 'cover',
      // 같은 오디오가 두 번 겹치면 울린다. PIP 는 그림만 쓴다.
      volume: 0,
    };
    try {
      working = apply(working, add);
      commands.push(add);
      applied++;
    } catch {
      // 한 컷이 실패해도 나머지 얼굴은 살린다.
    }
  }

  // 하나도 못 얹었으면 **아무것도 하지 않은 것으로** 되돌린다. 그러지 않으면 앞서 낸
  // remove 만 남아 «얼굴이 사라진 채로 저장»된다(적대적 리뷰 🟡7).
  if (applied === 0) return { edl, commands: [], applied: 0 };
  return { edl: working, commands, applied };
}
